import React from 'react';

import { get as getConfig } from '@lumapps/constants';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { actions } from '@lumapps/router/ducks/slice';

import { selectTotalUnreadCount } from '../ducks/unreadCounts';

const constants = getConfig();

interface UsePageTitleOptions {
    isPageLoading?: boolean;
    /** State qualifier appended to the title in the document title only. */
    titleStateQualifier?: string;
}

/**
 * Hook to dynamically set the html page title.
 * Uses the instance name as default title and appends it to given title if defined.
 */
export const usePageTitle = (title?: string, subtitle?: string, options: UsePageTitleOptions = {}) => {
    const { isPageLoading, titleStateQualifier } = options;
    const dispatch = useDispatch();
    const totalCount = useSelector(selectTotalUnreadCount);

    React.useEffect(() => {
        /**
         * If the page is currently loading, prevent update unless a title is specifically set.
         * This will ensure that titles are not updated or read during loading.
         */
        const shouldUpdate = !isPageLoading || title;
        const formatTitle = (pageTitlePart?: string) => {
            if (!pageTitlePart) {
                return constants.instanceName;
            }
            return subtitle
                ? `${subtitle} - ${pageTitlePart} - ${constants.instanceName}`
                : `${pageTitlePart} - ${constants.instanceName}`;
        };
        const pageTitle = formatTitle(title);
        const documentTitle = formatTitle(title && titleStateQualifier ? `${title} (${titleStateQualifier})` : title);

        /**
         * If a page hasn't been lazy loaded yet, it will be in "loading" state while the bundle downloads.
         * As the page title is set from the page itself, we cannot update the title during this transition.
         * Instead, we wait for the loading state to end before updating the title.
         */
        if (shouldUpdate) {
            document.title = totalCount > 0 ? `(${totalCount}) ${documentTitle}` : documentTitle;
        }
        dispatch(actions.setPageTitle(shouldUpdate ? pageTitle : ''));
    }, [isPageLoading, dispatch, subtitle, title, titleStateQualifier, totalCount]);
};
